import {
  Warning,
  Repeat,
  CalendarBlank,
  CaretDown,
  CaretUp,
  Info,
} from "@phosphor-icons/react";
import DatePicker from "../../../ui/DatePicker";
import TimePicker from "../../../ui/TimePicker";
import { RECURRING_FREQUENCY } from "../../../../store/services/calendarApi";
import { HEBREW_DAYS_FULL, formatHebrewDate } from "../../../../utils/dateUtils";
import { getFrequencyLabel } from "../../../../utils/appointmentSmsPreview";
import { getHolidayForDateStr } from "../../../../utils/israeliHolidays";
import TherapistConflictPanel from "./TherapistConflictPanel";

const WEEKDAY_OPTIONS = HEBREW_DAYS_FULL.map((label, value) => ({ value, label }));

// Meeting-series frequencies only. RECURRING_FREQUENCY also contains
// blocked-time-only values (daily / monthly_date / yearly) that must not be
// offered here.
const MEETING_FREQUENCIES = [
  RECURRING_FREQUENCY.WEEKLY,
  RECURRING_FREQUENCY.BIWEEKLY,
  RECURRING_FREQUENCY.MONTHLY,
  RECURRING_FREQUENCY.TWICE_WEEKLY,
];

// Compact holiday tag for a "YYYY-MM-DD" date (null on regular days)
const holidayTag = (dateStr) => {
  const h = dateStr ? getHolidayForDateStr(dateStr) : null;
  if (!h) return null;
  return { name: h.name, severity: h.severity };
};

/**
 * Step 2 — date/time, duration, single vs series (+ series config and an
 * editable occurrence preview). Overlap / past-date warnings confirm inline.
 */
const DURATION_SOURCE_LABEL = {
  patient: "משך מותאם אישית למטופל",
  treatmentType: "לפי סוג הטיפול",
  lastAppointment: "לפי הפגישה הקודמת",
};

function ScheduleStep({ form }) {
  const {
    formData,
    updateField,
    stepErrors,
    durationSource,
    isPastDateTime,
    pastDateConfirmed,
    setPastDateConfirmed,
    overlappingBlock,
    seriesPreview,
    setSeriesOverrides,
    recurringConflicts,
    allowConflictsAndResubmit,
    isSubmitting,
  } = form;

  const holiday = holidayTag(formData.date);
  const hasSelfConflict = recurringConflicts.some((c) => c.selfConflict);
  const dayName = formData.date
    ? HEBREW_DAYS_FULL[new Date(`${formData.date}T00:00:00`).getDay()]
    : null;

  const adjustDuration = (direction) => {
    const current = parseInt(formData.duration, 10);
    const base = Number.isFinite(current) ? current : 45;
    updateField("duration", Math.max(1, base + direction * 5));
  };

  // Derived end time — display only (start + duration)
  const endTime = (() => {
    const dur = parseInt(formData.duration, 10);
    if (!formData.startTime || !Number.isFinite(dur) || dur <= 0) return null;
    const [h, m] = formData.startTime.split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    const total = h * 60 + m + dur;
    return `${String(Math.floor(total / 60) % 24).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
  })();

  return (
    <div className="apt-wiz-step-panel" key="step-schedule">
      <div className="apt-wiz-form-row">
        <div className="apt-wiz-form-group">
          <label>תאריך{dayName ? ` — יום ${dayName}` : ""}</label>
          <DatePicker
            name="date"
            value={formData.date}
            onChange={(e) => updateField("date", e.target.value)}
            error={stepErrors.date}
          />
          {stepErrors.date && (
            <div className="apt-wiz-field-error">{stepErrors.date}</div>
          )}
        </div>
        <div className="apt-wiz-form-group">
          <label>שעה</label>
          <TimePicker
            name="startTime"
            value={formData.startTime}
            onChange={(e) => updateField("startTime", e.target.value)}
            error={stepErrors.startTime}
          />
          {stepErrors.startTime && (
            <div className="apt-wiz-field-error">{stepErrors.startTime}</div>
          )}
        </div>
      </div>

      <div className="apt-wiz-form-row">
      <div className="apt-wiz-form-group">
        <label>משך (דקות)</label>
        <div className="apt-wiz-amount-box apt-wiz-duration-box">
          <button
            type="button"
            className="apt-wiz-amount-btn"
            onClick={() => adjustDuration(-1)}
            aria-label="הפחתת 5 דקות"
          >
            <CaretDown size={14} />
          </button>
          <input
            type="number"
            inputMode="numeric"
            min="1"
            step="1"
            value={formData.duration}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") {
                updateField("duration", "");
                return;
              }
              const parsed = parseInt(raw, 10);
              if (Number.isFinite(parsed) && parsed >= 0) {
                updateField("duration", parsed);
              }
            }}
            onKeyDown={(e) => {
              if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
              e.preventDefault();
              adjustDuration(e.key === "ArrowUp" ? 1 : -1);
            }}
            className={stepErrors.duration ? "has-error" : ""}
            dir="ltr"
          />
          <button
            type="button"
            className="apt-wiz-amount-btn"
            onClick={() => adjustDuration(1)}
            aria-label="הוספת 5 דקות"
          >
            <CaretUp size={14} />
          </button>
        </div>
        {!stepErrors.duration &&
          !formData.isBlockedTime &&
          DURATION_SOURCE_LABEL[durationSource] && (
            <div className="apt-wiz-rate-hint">
              {DURATION_SOURCE_LABEL[durationSource]}
            </div>
          )}
        {stepErrors.duration && (
          <div className="apt-wiz-field-error">{stepErrors.duration}</div>
        )}
      </div>
      <div className="apt-wiz-form-group">
        <label>שעת סיום</label>
        <div className="apt-wiz-endtime-display" dir="ltr">
          {endTime || "—"}
        </div>
      </div>
      </div>

      {holiday && (
        <div className={`apt-wiz-hint-card ${holiday.severity === "red" ? "danger" : "warning"}`}>
          <Info size={14} weight="fill" />
          <span>
            {holiday.name} בתאריך זה
            {holiday.severity === "red" ? " — חג / יום שבתון" : " — ערב חג / חצי יום"}
          </span>
        </div>
      )}

      {/* Single vs series — create only; an existing meeting keeps its type */}
      {!form.isEditing && (
      <div className="apt-wiz-segment">
        <button
          type="button"
          className={`apt-wiz-segment-btn ${!formData.isRecurring ? "active" : ""}`}
          onClick={() => updateField("isRecurring", false)}
        >
          <CalendarBlank size={14} weight={formData.isRecurring ? "regular" : "fill"} />
          פגישה בודדת
        </button>
        <button
          type="button"
          className={`apt-wiz-segment-btn ${formData.isRecurring ? "active" : ""}`}
          onClick={() => updateField("isRecurring", true)}
        >
          <Repeat size={14} weight={formData.isRecurring ? "fill" : "regular"} />
          סדרת מפגשים
        </button>
      </div>
      )}

      {formData.isRecurring && !form.isEditing && (
        <div className="apt-wiz-series-config">
          <div className="apt-wiz-form-row">
            <div className="apt-wiz-form-group">
              <label>תדירות</label>
              <select
                value={formData.recurringFrequency}
                onChange={(e) => updateField("recurringFrequency", e.target.value)}
              >
                {MEETING_FREQUENCIES.map((freq) => (
                  <option key={freq} value={freq}>
                    {getFrequencyLabel(freq)}
                  </option>
                ))}
              </select>
            </div>
            <div className="apt-wiz-form-group">
              <label>מספר מפגשים</label>
              <input
                type="number"
                inputMode="numeric"
                min="2"
                max="100"
                value={formData.seriesTotalCount}
                onChange={(e) => updateField("seriesTotalCount", e.target.value)}
                className={`apt-wiz-num-input ${stepErrors.seriesTotalCount ? "has-error" : ""}`}
              />
              {stepErrors.seriesTotalCount && (
                <div className="apt-wiz-field-error">{stepErrors.seriesTotalCount}</div>
              )}
            </div>
          </div>

          {formData.recurringFrequency === RECURRING_FREQUENCY.TWICE_WEEKLY && (
            <div className="apt-wiz-form-row">
              <div className="apt-wiz-form-group">
                <label>יום שני בשבוע</label>
                <select
                  value={formData.recurringSecondDay}
                  onChange={(e) => updateField("recurringSecondDay", e.target.value)}
                >
                  <option value="">בחר/י יום</option>
                  {WEEKDAY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      יום {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="apt-wiz-form-group">
                <label>שעה למפגש השני</label>
                <TimePicker
                  name="recurringSecondTime"
                  value={formData.recurringSecondTime}
                  onChange={(e) => updateField("recurringSecondTime", e.target.value)}
                />
              </div>
              {stepErrors.recurringSecondDay && (
                <div className="apt-wiz-field-error full">
                  {stepErrors.recurringSecondDay}
                </div>
              )}
            </div>
          )}

          {seriesPreview.items.length > 0 && (
            <div className="apt-wiz-occurrences">
              <div className="apt-wiz-occurrences-title">
                {seriesPreview.items.length} מפגשים —{" "}
                {formatHebrewDate(seriesPreview.items[0].date, "dd/MM")} עד{" "}
                {formatHebrewDate(
                  seriesPreview.items[seriesPreview.items.length - 1].date,
                  "dd/MM"
                )}
              </div>
              <div className="apt-wiz-occurrence-list">
                  {seriesPreview.items.map((item, idx) => {
                    const occHoliday = holidayTag(item.dateStr);
                    return (
                      <div
                        key={idx}
                        className={`apt-wiz-occurrence-row ${item.isOverridden ? "overridden" : ""}`}
                      >
                        <div className="apt-wiz-occurrence-main">
                          <span className="apt-wiz-occurrence-num">{item.seriesIndex}</span>
                          <span className="apt-wiz-occurrence-day">
                            יום {HEBREW_DAYS_FULL[item.date.getDay()]}
                          </span>
                          <DatePicker
                            name={`occ-date-${idx}`}
                            value={item.dateStr}
                            onChange={(e) =>
                              setSeriesOverrides((prev) => ({
                                ...prev,
                                [idx]: { ...prev[idx], date: e.target.value },
                              }))
                            }
                          />
                          <TimePicker
                            name={`occ-time-${idx}`}
                            value={item.time}
                            onChange={(e) =>
                              setSeriesOverrides((prev) => ({
                                ...prev,
                                [idx]: { ...prev[idx], time: e.target.value },
                              }))
                            }
                          />
                        </div>
                        {occHoliday && (
                          <div
                            className={`apt-wiz-occurrence-holiday ${
                              occHoliday.severity === "red" ? "danger" : "warning"
                            }`}
                          >
                            <Warning size={12} weight="fill" />
                            <span>
                              {occHoliday.name}
                              {occHoliday.severity === "red"
                                ? " — חג / יום שבתון"
                                : " — ערב חג / חצי יום"}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
            </div>
          )}
        </div>
      )}

      {/* Inline warnings & confirmation gates */}
      {overlappingBlock && (
        <div className="apt-wiz-hint-card warning">
          <Info size={14} weight="fill" />
          <span>
            הזמן חופף לחלון חסום ביומן ({overlappingBlock.start}–{overlappingBlock.end})
          </span>
        </div>
      )}

      {isPastDateTime && !form.isEditing && (
        <div className={`apt-wiz-gate-card ${pastDateConfirmed ? "confirmed" : ""}`}>
          <div className="apt-wiz-gate-text">
            <Warning size={15} weight="fill" />
            <span>
              המועד שנבחר כבר עבר — הפגישה תיווצר כפגישה שהושלמה, ללא הודעות SMS.
            </span>
          </div>
          {!pastDateConfirmed ? (
            <button
              type="button"
              className="apt-wiz-gate-btn"
              onClick={() => setPastDateConfirmed(true)}
            >
              אישור
            </button>
          ) : (
            <span className="apt-wiz-gate-ok">אושר</span>
          )}
        </div>
      )}

      {/* Therapist double-booking + absence (create AND edit, own and others') */}
      <TherapistConflictPanel form={form} />

      {recurringConflicts.length > 0 && (
        <div className="apt-wiz-conflict-panel">
          <div className="apt-wiz-gate-text">
            <Warning size={15} weight="fill" />
            <span>
              {hasSelfConflict
                ? "נמצאו התנגשויות בתוך הסדרה עצמה — יש לשנות את מועדי הסדרה"
                : `נמצאו ${recurringConflicts.length} התנגשויות עם פגישות קיימות`}
            </span>
          </div>
          <ul className="apt-wiz-conflict-list">
            {recurringConflicts.slice(0, 3).map((c, i) => (
              <li key={i}>
                {c.seriesIndex ? `מפגש ${c.seriesIndex} · ` : ""}
                {c.date
                  ? `${formatHebrewDate(new Date(c.date), "dd/MM")} ${new Date(c.date)
                      .toTimeString()
                      .slice(0, 5)}`
                  : ""}
                {c.existingAppointment?.patientName
                  ? ` — מול ${c.existingAppointment.patientName}`
                  : ""}
              </li>
            ))}
            {recurringConflicts.length > 3 && (
              <li>ועוד {recurringConflicts.length - 3} התנגשויות…</li>
            )}
          </ul>
          {!hasSelfConflict && form.canOverrideTherapistConflict !== false && (
            <button
              type="button"
              className="apt-wiz-gate-btn"
              onClick={allowConflictsAndResubmit}
              disabled={isSubmitting}
            >
              {form.isEditing ? "עדכן בכל זאת" : "צור בכל זאת"}
            </button>
          )}
          {!hasSelfConflict && form.canOverrideTherapistConflict === false && (
            <span className="apt-wiz-gate-text apt-wiz-gate-blocked">
              מדיניות הקליניקה חוסמת כפל תורים למטפל/ת. רק מנהל/ת יכול/ה לעקוף.
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default ScheduleStep;
